import { z } from 'zod';
import { emailSchema } from './common.dto';
import { UserRole } from '../enums';

/**
 * Auth DTOs. Email+password (argon2id), JWT access+refresh, mandatory TOTP 2FA for staff.
 */

/** Password policy: >= 12 chars, with upper, lower, digit. */
export const passwordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .max(128)
  .regex(/[a-z]/, 'Must contain a lowercase letter')
  .regex(/[A-Z]/, 'Must contain an uppercase letter')
  .regex(/[0-9]/, 'Must contain a digit');

export const totpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'TOTP code must be 6 digits');

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1),
  /** Required once 2FA is enrolled; the API signals when it is needed. */
  totpCode: totpCodeSchema.optional(),
});
export type LoginDto = z.infer<typeof loginSchema>;

export const refreshSchema = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshDto = z.infer<typeof refreshSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: passwordSchema,
});
export type ChangePasswordDto = z.infer<typeof changePasswordSchema>;

/** Submit the TOTP code during first-login enrollment to confirm the secret. */
export const enroll2faSchema = z.object({
  totpCode: totpCodeSchema,
});
export type Enroll2faDto = z.infer<typeof enroll2faSchema>;

/** Log in using a one-time recovery code instead of the authenticator. */
export const recoveryLoginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1),
  recoveryCode: z.string().trim().min(1),
});
export type RecoveryLoginDto = z.infer<typeof recoveryLoginSchema>;

export const authTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
});
export type AuthTokensDto = z.infer<typeof authTokensSchema>;

/** JWT access-token payload contract. */
export const jwtPayloadSchema = z.object({
  sub: z.string(),
  role: z.nativeEnum(UserRole),
  email: emailSchema,
});
export type JwtPayload = z.infer<typeof jwtPayloadSchema>;
